import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Mail, Lock, AlertCircle, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../services/api';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const buildClientRedirect = () => {
    const returnTo = searchParams.get('returnTo');
    if (returnTo && returnTo.startsWith('/')) return returnTo;
    const action = searchParams.get('action');
    const promo = searchParams.get('promo');
    let url = '/client';
    const params = new URLSearchParams();
    if (action) params.set('action', action);
    if (promo) params.set('promo', promo);
    if (params.toString()) url += '?' + params.toString();
    return url;
  };

  // Redirect if already logged in
  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'vendor') {
        navigate('/vendor');
      } else if (user.role === 'influencer') {
        navigate('/influencer');
      } else {
        navigate(buildClientRedirect());
      }
    }
  }, [isAuthenticated, user, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNeedsVerification(false);
    setLoading(true);

    try {
      const result = await login({ email, password });
      const loggedUser = result.user;
      
      if (loggedUser.role === 'admin') {
        navigate('/admin');
      } else if (loggedUser.role === 'vendor') {
        navigate('/vendor');
      } else if (loggedUser.role === 'influencer') {
        navigate('/influencer');
      } else {
        navigate(buildClientRedirect());
      }
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 'Erreur de connexion. Vérifiez vos identifiants.';
      setError(errorMsg);
      
      if (errorMsg.includes('vérifier votre email') || errorMsg.includes('verify your email')) {
        setNeedsVerification(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      await api.post('/auth/resend-verification', null, { params: { email: email.trim().toLowerCase() } });
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err) {
      const detail = err.response?.data?.detail || "Erreur lors du renvoi de l'email";
      setError(detail);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="login-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-[#1A1A1D] p-8 rounded-lg metallic-border cyber-glow">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">Connexion</h1>
            <p className="text-[#B8B8BA]">Accédez à votre espace IPTV4KFR</p>
          </div>

          {error && (
            <div className="bg-[#FF3B5C]/10 border border-[#FF3B5C] rounded-lg p-4 mb-6 flex items-start space-x-3" data-testid="error-message">
              <AlertCircle className="h-5 w-5 text-[#FF3B5C] flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <span className="text-[#FF3B5C] text-sm">{error}</span>
                {needsVerification && (
                  <div className="mt-3">
                    <button
                      onClick={handleResendVerification}
                      className="btn-primary py-2 px-4 text-sm"
                      data-testid="resend-verification-btn"
                    >
                      Renvoyer l&apos;email de vérification
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {resendSuccess && (
            <div className="bg-[#00FF88]/10 border border-[#00FF88] rounded-lg p-4 mb-6 flex items-start space-x-3" data-testid="resend-success">
              <CheckCircle className="h-5 w-5 text-[#00FF88] flex-shrink-0 mt-0.5" />
              <div>
                <span className="text-[#00FF88] text-sm">Email de v&eacute;rification renvoy&eacute; ! V&eacute;rifiez votre bo&icirc;te de r&eacute;ception.</span>
                <p className="text-[#FFB800] text-xs mt-1">Pensez &agrave; v&eacute;rifier votre dossier Spam / Courrier ind&eacute;sirable.</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-[#E8E8E8] font-medium mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#B8B8BA]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value.toLowerCase())}
                  className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg pl-12 pr-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none transition-colors lowercase"
                  placeholder="votre@email.com"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-[#E8E8E8] font-medium mb-2">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#B8B8BA]" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-[#222226] border border-[#3A3A3D] rounded-lg pl-12 pr-4 py-3 text-white focus:border-[#00FFFF] focus:outline-none transition-colors"
                  placeholder="••••••••"
                  required
                  data-testid="password-input"
                />
              </div>
              <div className="text-right mt-2">
                <Link 
                  to="/mot-de-passe-oublie" 
                  className="text-[#00FFFF] text-sm hover:underline"
                  data-testid="forgot-password-link"
                >
                  Mot de passe oublié ?
                </Link>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary cyber-glow-hover disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-button"
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#B8B8BA]">
              Pas encore de compte ?{' '}
              <Link to="/inscription" className="text-[#00FFFF] hover:underline" data-testid="register-link">
                S&apos;inscrire
              </Link>
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Login;